export class _Literal<ValueType> { 
    x: ValueType
    constructor(
        x: ValueType
    ) {
        this.x = x;
    }
    evaluate<ArgTypes>(args: ArgTypes): ValueType {
        return this.x;
    }
}

export function Literal<ValueType>(x: ValueType): _Literal<ValueType> {
    return new _Literal(x);
}

export class _Symbol<ArgTypes, ValueType> { 
    name: string
    constructor(
        name: string
    ) {
        this.name = name;
    }
    evaluate(args: ArgTypes): ValueType {
        const asAny:any = args;
        return asAny[this.name];
    }
}

export function Symbol<ArgTypes, ValueType>(name: string): _Symbol<ArgTypes, ValueType> {
    return new _Symbol(name);
}

export class _Comparison2<ArgTypes, ValueType> { 
    operator: 'eq'|'ne'|'gt'|'lt'|'le'|'ge';
    a: ArithmeticTerm<ArgTypes, ValueType>;
    b: ArithmeticTerm<ArgTypes, ValueType>;

    constructor(
        operator: 'eq'|'ne'|'gt'|'lt'|'le'|'ge',
        a: ArithmeticTerm<ArgTypes, ValueType>, 
        b: ArithmeticTerm<ArgTypes, ValueType>
    ) {
        this.operator = operator;
        this.a = a;
        this.b = b;
    }

    evaluate<ArgTypes>(args: ArgTypes): boolean|undefined {
        if(this.operator === 'eq') {
            return this.a.evaluate(args) === this.b.evaluate(args);
        } else if(this.operator === 'ne') {
            return this.a.evaluate(args) !== this.b.evaluate(args);
        } else if(this.operator === 'lt') {
            return this.a.evaluate(args) < this.b.evaluate(args);
        } else if(this.operator === 'gt') {
            return this.a.evaluate(args) > this.b.evaluate(args);
        } else if(this.operator === 'le') {
            return this.a.evaluate(args) <= this.b.evaluate(args);
        } else if(this.operator === 'ge') {
            return this.a.evaluate(args) >= this.b.evaluate(args);
        }
    }
}

export function EQ<ArgTypes, ValueType>(a: ArithmeticTerm<ArgTypes, ValueType>, b: ArithmeticTerm<ArgTypes, ValueType>): _Comparison2<ArgTypes, ValueType> {
    return new _Comparison2('eq', a, b);
}
export function NE<ArgTypes, ValueType>(a: ArithmeticTerm<ArgTypes, ValueType>, b: ArithmeticTerm<ArgTypes, ValueType>): _Comparison2<ArgTypes, ValueType> {
    return new _Comparison2('ne', a, b);
}
export function GT<ArgTypes, ValueType>(a: ArithmeticTerm<ArgTypes, ValueType>, b: ArithmeticTerm<ArgTypes, ValueType>): _Comparison2<ArgTypes, ValueType> {
    return new _Comparison2('gt', a, b);
}
export function LT<ArgTypes, ValueType>(a: ArithmeticTerm<ArgTypes, ValueType>, b: ArithmeticTerm<ArgTypes, ValueType>): _Comparison2<ArgTypes, ValueType> {
    return new _Comparison2('lt', a, b);
}
export function GE<ArgTypes, ValueType>(a: ArithmeticTerm<ArgTypes, ValueType>, b: ArithmeticTerm<ArgTypes, ValueType>): _Comparison2<ArgTypes, ValueType> {
    return new _Comparison2('ge', a, b);
}
export function LE<ArgTypes, ValueType>(a: ArithmeticTerm<ArgTypes, ValueType>, b: ArithmeticTerm<ArgTypes, ValueType>): _Comparison2<ArgTypes, ValueType> {
    return new _Comparison2('le', a, b);
}

export class _LogicalN<ArgTypes, ValueType extends boolean> { 
    operator: 'and'|'or'|'xor'|'xnor';
    x: LogicalTerm<ArgTypes, ValueType>[];

    constructor(
        operator: 'and'|'or'|'xor'|'xnor',
        x: LogicalTerm<ArgTypes, ValueType>[]
    ) {
        this.operator = operator;
        this.x = x;
    }

    evaluate<ArgTypes>(args: ArgTypes): boolean|undefined {
        return this.x.reduce((result: boolean|undefined, term: LogicalTerm<ArgTypes, ValueType>) => {
            if(result === undefined) {
                return term.evaluate(args);
            } else if(this.operator === 'and') {
                return result && term.evaluate(args);
            } else if(this.operator === 'or') {
                return result || term.evaluate(args);
            } else if(this.operator === 'xor') {
                return result !== term.evaluate(args);
            } else if(this.operator === 'xnor') {
                return result === term.evaluate(args);
            }
        }, undefined);
    }
}

export function AND<ArgTypes, ValueType extends boolean>(... x: LogicalTerm<ArgTypes, ValueType>[]): _LogicalN<ArgTypes, ValueType> {
    return new _LogicalN('and', x);
}
export function OR<ArgTypes, ValueType extends boolean>(... x: LogicalTerm<ArgTypes, ValueType>[]): _LogicalN<ArgTypes, ValueType> {
    return new _LogicalN('or', x);
}
export function XOR<ArgTypes, ValueType extends boolean>(... x: LogicalTerm<ArgTypes, ValueType>[]): _LogicalN<ArgTypes, ValueType> {
    return new _LogicalN('xor', x);
}
export function XNOR<ArgTypes, ValueType extends boolean>(... x: LogicalTerm<ArgTypes, ValueType>[]): _LogicalN<ArgTypes, ValueType> {
    return new _LogicalN('xnor', x);
}

export class _Logical1<ArgTypes, ValueType extends boolean> { 
    operator: 'not';
    x: LogicalTerm<ArgTypes, ValueType>;
    constructor(
        operator: 'not',
        x: LogicalTerm<ArgTypes, ValueType>
    ) {
        this.operator = operator;
        this.x = x;
    }
    evaluate<ArgTypes>(args: ArgTypes): boolean {
        return !this.x.evaluate(args);
    }
}

export function NOT<ArgTypes, ValueType extends boolean>(x: LogicalTerm<ArgTypes, ValueType>): _Logical1<ArgTypes, ValueType> {
    return new _Logical1('not', x);
}

export class _ArithmeticN<ArgTypes, ValueType extends any> { 
    operator: 'add'|'sub'|'mul';
    x: ArithmeticTerm<ArgTypes, ValueType>[];
    constructor(
        operator: 'add'|'sub'|'mul',
        x: ArithmeticTerm<ArgTypes, ValueType>[]
    ) {
        this.operator = operator;
        this.x = x;
    }
    evaluate<ArgTypes>(args: ArgTypes): ValueType {
        return this.x.reduce((result: any, term: ArithmeticTerm<ArgTypes, ValueType>) => {
            if(result === undefined) {
                return term.evaluate(args);
            } else if(this.operator === 'add') {
                return result + term.evaluate(args);
            } else if(this.operator === 'sub') {
                return result - term.evaluate(args);
            } else if(this.operator === 'mul') {
                return result * term.evaluate(args);
            } else {
                throw new Error('Undefined case should never occur');
            }
        }, undefined);
    }
}
export function ADD<ArgTypes, ValueType extends any>(... x: ArithmeticTerm<ArgTypes, ValueType>[]) {
    return new _ArithmeticN('add', x);
}
export function SUB<ArgTypes, ValueType extends any>(... x: ArithmeticTerm<ArgTypes, ValueType>[]) {
    return new _ArithmeticN('sub', x);
}
export function MUL<ArgTypes, ValueType extends any>(... x: ArithmeticTerm<ArgTypes, ValueType>[]) {
    return new _ArithmeticN('mul', x);
}
export class _Arithmetic2<ArgTypes, ValueType extends any> { 
    operator: 'div'|'mod'|'pow';
    a: ArithmeticTerm<ArgTypes, ValueType>;
    b: ArithmeticTerm<ArgTypes, ValueType>;
    constructor(
        operator: 'div'|'mod'|'pow',
        a: ArithmeticTerm<ArgTypes, ValueType>,
        b: ArithmeticTerm<ArgTypes, ValueType>
    ) {
        this.operator = operator;
        this.a = a;
        this.b = b;
    }
    evaluate<ArgTypes>(args: ArgTypes): any {
        if(this.operator === 'div') {
            return this.a.evaluate(args) / this.b.evaluate(args);
        } else if(this.operator === 'mod') {
            return this.a.evaluate(args) % this.b.evaluate(args);
        } else if(this.operator === 'pow') {
            return this.a.evaluate(args) ** this.b.evaluate(args);
        } else {
            throw new Error('Undefined case should never occur');
        }
    }
}

export function DIV<ArgTypes, ValueType extends any>(a: ArithmeticTerm<ArgTypes, ValueType>, b: ArithmeticTerm<ArgTypes, ValueType>): _Arithmetic2<ArgTypes, ValueType> {
    return new _Arithmetic2('div', a, b);
}
export function MOD<ArgTypes, ValueType extends any>(a: ArithmeticTerm<ArgTypes, ValueType>, b: ArithmeticTerm<ArgTypes, ValueType>): _Arithmetic2<ArgTypes, ValueType> {
    return new _Arithmetic2('mod', a, b);
}
export function POW<ArgTypes, ValueType extends any>(a: ArithmeticTerm<ArgTypes, ValueType>, b: ArithmeticTerm<ArgTypes, ValueType>): _Arithmetic2<ArgTypes, ValueType> {
    return new _Arithmetic2('pow', a, b);
}

export type ArithmeticTerm<ArgTypes, ResultType> = _ArithmeticN<ArgTypes, ResultType> | _Arithmetic2<ArgTypes, ResultType> | _Literal<ResultType>;

export type LogicalTerm<ArgTypes, ResultType extends boolean> = _LogicalN<ArgTypes, ResultType> | _Logical1<ArgTypes, ResultType> | _Literal<ResultType>;

const example = AND(Literal(false), Literal(true))
